import React, { createContext, useContext, useMemo } from "react";
import { Button } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/*
 * Shared between the table (which renders the sortable <tr>) and the columns
 * file (which renders the grip handle inside a cell). Keeping the context here
 * avoids a circular import between the page and its column definitions.
 */
export const RowContext = createContext({});

export const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      ref={setActivatorNodeRef}
      style={{ cursor: "move" }}
      {...listeners}
    />
  );
};

const DraggableRow = (props) => {
  // antd passes the row key in its original type (a number here), but
  // SortableContext `items` are strings. dnd-kit compares ids strictly, so we
  // must coerce to string for the row to match its sortable entry. Falls back to
  // a stable id for antd's keyless "No Data" placeholder row.
  const rawKey = props["data-row-key"];
  const rowKey = rawKey != null ? String(rawKey) : "__placeholder__";
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowKey });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 999 } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

export default DraggableRow;
